// ===========================================
// Meet & Greet notes API (#255, enquiries Phase 4)
// ===========================================
// The teacher's own Meet & Greets and the notes a teacher (or the office, on their behalf) records.
// The contract is the backend's: `routes/teacherMeetAndGreet.routes.ts`, `services/meetAndGreet/
// notes.service.ts` (the view shapes) and `validators/meetAndGreetNotes.validators.ts` (the body).
// The teacher view carries NO email: contact 1's name and phone only.

import { apiClient } from '../services/api';
import type { MeetAndGreetStatus } from './meetAndGreet.api';

export type NoteAttendance = 'ATTENDED' | 'NO_SHOW';
export type NoteEngagement = 'LOW' | 'MEDIUM' | 'HIGH';
export type NoteOutcome = 'FOLLOW_UP' | 'YES_ON_THE_DAY' | 'NOT_SUITABLE';

/** An append-only note. `performedById` and `schoolId` never leave the server. */
export interface MeetAndGreetNote {
  id: string;
  scheduledDateTime: string | null;
  attendance: NoteAttendance;
  engagement: NoteEngagement | null;
  priorExperience: string | null;
  observations: string | null;
  recommendedProgram: string | null;
  recommendedClass: string | null;
  notesForTeam: string | null;
  outcome: NoteOutcome | null;
  performedByName: string;
  createdAt: string;
}

/** `GET /teacher/meet-and-greets` and `/:id`: the one narrow shape a teacher is shown. */
export interface TeacherMeetAndGreetView {
  id: string;
  status: MeetAndGreetStatus;
  scheduledDateTime: string | null;
  studentFirstName: string;
  studentLastName: string;
  studentAge: number;
  instrument: { id: string; name: string } | null;
  additionalNotes: string | null;
  /** Contact 1 only: name and phone. There is deliberately no email. */
  contactName: string;
  contactPhone: string;
  /** The linked enquiry's original message (plain text), null when none is linked. */
  enquiryMessage: string | null;
  notesMissing: boolean;
  canRecordNotes: boolean;
  completedAt: string | null;
  /** Newest first. */
  notes: MeetAndGreetNote[];
}

/** The POST body. A NO_SHOW sends no `outcome` key at all. */
export interface RecordMeetAndGreetNotesInput {
  attendance: NoteAttendance;
  engagement?: NoteEngagement | null;
  priorExperience?: string | null;
  observations?: string | null;
  recommendedProgram?: string | null;
  recommendedClass?: string | null;
  notesForTeam?: string | null;
  outcome?: NoteOutcome;
}

export interface RecordedNotesResult {
  note: MeetAndGreetNote;
  completedAt: string | null;
}

interface ApiResponse<T> {
  status: string;
  data: T;
}

export const teacherMeetAndGreetApi = {
  list: (needsNotes?: boolean): Promise<TeacherMeetAndGreetView[]> =>
    apiClient
      .get<ApiResponse<TeacherMeetAndGreetView[]>>('/teacher/meet-and-greets', {
        params: needsNotes ? { needsNotes: true } : undefined,
      })
      .then((res) => res.data),

  get: (id: string): Promise<TeacherMeetAndGreetView> =>
    apiClient.get<ApiResponse<TeacherMeetAndGreetView>>(`/teacher/meet-and-greets/${id}`).then((res) => res.data),

  recordNotes: (id: string, input: RecordMeetAndGreetNotesInput): Promise<RecordedNotesResult> =>
    apiClient
      .post<ApiResponse<RecordedNotesResult>>(`/teacher/meet-and-greets/${id}/notes`, input)
      .then((res) => res.data),
};

export const adminMeetAndGreetNotesApi = {
  list: (id: string): Promise<MeetAndGreetNote[]> =>
    apiClient.get<ApiResponse<MeetAndGreetNote[]>>(`/admin/meet-and-greet/${id}/notes`).then((res) => res.data),

  recordNotes: (id: string, input: RecordMeetAndGreetNotesInput): Promise<RecordedNotesResult> =>
    apiClient
      .post<ApiResponse<RecordedNotesResult>>(`/admin/meet-and-greet/${id}/notes`, input)
      .then((res) => res.data),
};
